import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { PageBreadcrumbComponent } from '../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { ButtonComponent } from '../../shared/components/ui/button/button.component';
import { CountrySelectComponent } from '../../shared/components/form/country-select/country-select.component';
import { COUNTRIES_DATA, CountryData, countryFromPhone } from '../../shared/data/countries.data';
import { AuthService } from '../auth-pages/services/auth.service';
import { ProfileApiService } from './services/profile-api.service';
import { User } from '../auth-pages/models/user.model';
import { DatetimeService } from '../../shared/services/datetime.service';
import { ToastService } from '../../shared/services/toast.service';
import { ConfirmDialogService } from '../../shared/services/confirm-dialog.service';

/** Tamaño máximo de la foto de perfil. El backend admite 10 MB, pero el diseño pide 2. */
const MAX_AVATAR_BYTES = 2 * 1024 * 1024;
const ALLOWED_AVATAR_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

/**
 * INC_CRM_013 inc2/inc5 — "Editar perfil".
 *
 * Contiene únicamente los datos de la persona: foto, información personal y zona
 * horaria. Todo lo que es seguridad o integraciones (cuentas de correo, contraseña,
 * eliminar cuenta) vive en `AccountSettingsComponent` (`/account-settings`).
 */
@Component({
    selector: 'app-profile',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        PageBreadcrumbComponent,
        ButtonComponent,
        CountrySelectComponent
    ],
    templateUrl: './profile.component.html',
    styleUrl: './profile.component.css'
})
export class ProfileComponent implements OnInit {
    readonly countries: CountryData[] = COUNTRIES_DATA;
    readonly bioMaxLength = 160;

    currentUser: User | null = null;
    profileForm!: FormGroup;

    savingProfile = false;

    // Foto de perfil
    avatarUrl: string | null = null;
    uploadingAvatar = false;

    // Teléfono: el backend lo guarda en un solo campo con el prefijo incluido
    // ("+51 987654321"); aquí se edita partido en prefijo + número.
    phoneCountryCode = '';

    // Zona horaria
    selectedTimezone = '';
    timezoneSearch = '';
    allTimezones: string[] = [];
    savingTimezone = false;

    constructor(
        private fb: FormBuilder,
        private authService: AuthService,
        private profileApi: ProfileApiService,
        private router: Router,
        private route: ActivatedRoute,
        private datetimeService: DatetimeService,
        private toast: ToastService,
        private confirmDialog: ConfirmDialogService
    ) {
        this.initForm();
    }

    ngOnInit(): void {
        this.allTimezones = this.datetimeService.getSupportedTimezones();
        this.redirectMailOAuthReturn();
        this.loadProfile();
    }

    /**
     * Las cuentas de correo se conectan desde Configuración de cuenta, pero un backend
     * con la configuración antigua (`communication.oauth.frontend.success-url` → /#/profile)
     * devuelve al usuario aquí. Se reenvía a la vista correcta conservando el resultado.
     */
    private redirectMailOAuthReturn(): void {
        const params = this.route.snapshot.queryParamMap;
        if (!params.get('status')) return;

        this.router.navigate(['/account-settings'], {
            queryParams: {
                status: params.get('status'),
                provider: params.get('provider'),
                email: params.get('email'),
                message: params.get('message')
            },
            replaceUrl: true
        });
    }

    private initForm(): void {
        this.profileForm = this.fb.group({
            fullname: ['', [Validators.required, Validators.maxLength(100)]],
            lastname: ['', Validators.maxLength(100)],
            email: [{ value: '', disabled: true }],
            phoneNumber: ['', Validators.maxLength(30)],
            jobTitle: ['', Validators.maxLength(100)],
            bio: ['', Validators.maxLength(this.bioMaxLength)]
        });
    }

    private loadProfile(): void {
        this.profileApi.getProfile().subscribe({
            next: (user) => {
                this.currentUser = user;
                this.loadUserData(user);
                this.authService.updateCurrentUser(user);
            },
            error: () => {
                const user = this.authService.getCurrentUser();
                if (user) {
                    this.currentUser = user;
                    this.loadUserData(user);
                } else {
                    this.router.navigate(['/signin']);
                }
            }
        });
    }

    private loadUserData(user: User): void {
        const country = countryFromPhone(user.phone);
        this.phoneCountryCode = country?.code ?? user.countryCode ?? '';

        this.profileForm.patchValue({
            fullname: user.fullname,
            lastname: user.lastname || '',
            email: user.email,
            phoneNumber: this.stripDialCode(user.phone, country),
            jobTitle: user.jobTitle || '',
            bio: user.bio || ''
        });

        this.avatarUrl = user.avatarUrl || null;
        this.selectedTimezone = user.timezone || '';
    }

    /** Quita el prefijo internacional para editar solo el número local. */
    private stripDialCode(phone: string | null | undefined, country: CountryData | null): string {
        const value = (phone ?? '').trim();
        if (!value) return '';
        if (country?.dialCode && value.startsWith(country.dialCode)) {
            return value.slice(country.dialCode.length).trim();
        }
        return value;
    }

    /** Recompone el teléfono completo para enviarlo al backend. */
    private buildPhone(): string {
        const number = (this.profileForm.getRawValue().phoneNumber || '').trim();
        if (!number) return '';
        const dialCode = this.countries.find(c => c.code === this.phoneCountryCode)?.dialCode;
        return dialCode ? `${dialCode} ${number}` : number;
    }

    onPhoneCountryChange(code: string): void {
        this.phoneCountryCode = code;
        this.profileForm.markAsDirty();
    }

    get bioLength(): number {
        return (this.profileForm.get('bio')?.value || '').length;
    }

    // ── Foto de perfil ──

    onAvatarSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        // Se limpia el input para que volver a elegir el MISMO archivo dispare el evento.
        input.value = '';
        if (!file) return;

        if (!ALLOWED_AVATAR_TYPES.includes(file.type)) {
            this.toast.warning({
                title: 'Formato no admitido',
                message: 'La foto debe ser JPG, PNG, GIF o WebP.'
            });
            return;
        }

        if (file.size > MAX_AVATAR_BYTES) {
            this.toast.warning({
                title: 'Imagen demasiado grande',
                message: 'La foto no puede superar los 2 MB.'
            });
            return;
        }

        const userId = this.currentUser?.id;
        if (!userId) return;

        this.uploadingAvatar = true;
        this.profileApi.uploadAvatar(userId, file).subscribe({
            next: (user) => {
                this.uploadingAvatar = false;
                this.applyUpdatedUser(user);
                this.toast.updated('Foto de perfil');
            },
            error: (error) => {
                this.uploadingAvatar = false;
                this.toast.error({
                    title: 'Error',
                    message: this.describeAvatarError(error, 'No se pudo actualizar la foto de perfil. Inténtalo nuevamente.')
                });
            }
        });
    }

    async onRemoveAvatar(): Promise<void> {
        const userId = this.currentUser?.id;
        if (!userId || !this.avatarUrl) return;

        const ok = await this.confirmDialog.confirmDanger({
            title: 'Eliminar foto de perfil',
            message: '¿Seguro que deseas eliminar tu foto de perfil?',
            confirmText: 'Eliminar foto'
        });
        if (!ok) return;

        this.uploadingAvatar = true;
        this.profileApi.deleteAvatar(userId).subscribe({
            next: () => {
                this.uploadingAvatar = false;
                this.avatarUrl = null;
                if (this.currentUser) {
                    const updated: User = { ...this.currentUser, avatarUrl: null, avatarFileId: null };
                    this.currentUser = updated;
                    this.authService.updateCurrentUser(updated);
                }
                this.toast.deleted('Foto de perfil');
            },
            error: (error) => {
                this.uploadingAvatar = false;
                this.toast.error({
                    title: 'Error',
                    message: this.describeAvatarError(error, 'No se pudo eliminar la foto de perfil. Inténtalo nuevamente.')
                });
            }
        });
    }

    /**
     * INC_CRM_013 inc6 — el mensaje del backend NO se muestra tal cual: los errores de
     * autorización llegan en inglés y detallando la jerarquía de roles ("Required role: User
     * (order=5), Your role order: 99..."), que ni es accionable ni es información que deba ver el
     * usuario. Solo se traducen los casos que el usuario sí puede corregir.
     */
    private describeAvatarError(error: unknown, porDefecto: string): string {
        const status = (error as { status?: number })?.status;

        if (status === 403) {
            return 'No tienes permiso para cambiar esta foto de perfil.';
        }
        if (status === 413) {
            return 'La foto es demasiado grande. Elige una imagen de menos de 2 MB.';
        }
        if (status === 400) {
            return 'La imagen no es válida. Debe ser JPG, PNG, GIF o WebP de menos de 2 MB.';
        }

        return porDefecto;
    }

    /** Iniciales para el placeholder cuando el usuario no tiene foto. */
    get avatarInitials(): string {
        const first = (this.currentUser?.fullname || '').trim().charAt(0);
        const last = (this.currentUser?.lastname || '').trim().charAt(0);
        return (first + last).toUpperCase() || 'U';
    }

    // ── Zona horaria ──

    get filteredTimezones(): string[] {
        if (!this.timezoneSearch) return this.allTimezones;
        const term = this.timezoneSearch.toLowerCase();
        return this.allTimezones.filter(tz => tz.toLowerCase().includes(term));
    }

    onTimezoneChange(tz: string): void {
        this.selectedTimezone = tz;
        this.savingTimezone = true;

        this.profileApi.updateTimezone({ timezone: tz || null }).subscribe({
            next: (user) => {
                this.savingTimezone = false;
                this.applyUpdatedUser(user);
                this.datetimeService.setUserTimezone(tz || null);
                this.toast.updated('Zona horaria');
            },
            error: () => {
                this.savingTimezone = false;
                this.toast.error({ title: 'Error', message: 'No se pudo actualizar la zona horaria.' });
            }
        });
    }

    /** Hora actual en la zona seleccionada, como referencia bajo el selector. */
    get timezonePreview(): string {
        try {
            return new Intl.DateTimeFormat('es', {
                dateStyle: 'medium',
                timeStyle: 'short',
                timeZone: this.selectedTimezone || undefined
            }).format(new Date());
        } catch {
            return '';
        }
    }

    // ── Guardar ──

    onUpdateProfile(): void {
        if (this.profileForm.invalid) {
            this.profileForm.markAllAsTouched();
            this.toast.warning({
                title: 'Revisa el formulario',
                message: 'Hay campos con errores o pendientes de completar.'
            });
            return;
        }

        this.savingProfile = true;
        const raw = this.profileForm.getRawValue();

        this.profileApi.updateProfile({
            fullname: raw.fullname,
            lastname: raw.lastname,
            email: raw.email,
            phone: this.buildPhone(),
            jobTitle: (raw.jobTitle || '').trim(),
            bio: (raw.bio || '').trim()
        }).subscribe({
            next: (user) => {
                this.savingProfile = false;
                this.applyUpdatedUser(user);
                this.profileForm.markAsPristine();
                this.toast.updated('Perfil');
            },
            error: (error) => {
                this.savingProfile = false;
                this.toast.error({
                    title: 'Error',
                    message: error.error?.message || error.error?.mensaje || 'No se pudo actualizar el perfil.'
                });
            }
        });
    }

    /** Refresca el usuario en sesión para que el header (nombre y foto) se actualice al instante. */
    private applyUpdatedUser(user: User): void {
        this.currentUser = user;
        this.avatarUrl = user.avatarUrl || null;
        this.authService.updateCurrentUser(user);
    }

    isFieldInvalid(fieldName: string): boolean {
        const field = this.profileForm.get(fieldName);
        return !!(field && field.invalid && (field.dirty || field.touched));
    }

    getFieldError(fieldName: string): string {
        const field = this.profileForm.get(fieldName);
        if (!field || !field.errors) return '';

        if (field.errors['required']) return 'Este campo es requerido';
        if (field.errors['email']) return 'Email inválido';
        if (field.errors['maxlength']) return `Máximo ${field.errors['maxlength'].requiredLength} caracteres`;

        return '';
    }
}
